'use client';
// @ts-ignore — plain-JS Hebrew message builder (LAW 04/10/26)
import { callLabel } from '@/lib/heMsg.mjs';
import { useState } from 'react';
import { fireContactIntent } from '@/lib/contactTracking';
import { contactViaCompany } from '@/lib/directContact';
import { setContacted } from '@/lib/contactedState';
import { useSmartIntakeCtx, resolvePageCity } from '@/lib/smartIntakeStore';
import { ctxIsSameCategory } from '@/lib/waMessage';

type BtnProps = {
  proName: string; proFirstName?: string; proPhone?: string; proId?: number; proGender: string | null | undefined; categorySlug?: string; categoryName?: string; className?: string; label?: string;
  /** ?layout=3.x preview skin (lib/topicLayout.ts): the SAME click handler/routing, a different look. Omitted = unchanged. */
  skin?: { label: string; className?: string; style?: React.CSSProperties; children: React.ReactNode };

  // Square icon-on-top/label-below button (2026-08-23, CategoryProCard mobile column
  // redesign) — pairs with WaSmartButton's own `square` variant side by side.
  square?: boolean;
  // Round icon-only floating button (2026-08-23, ProFloatingContact) — pairs with
  // WaSmartButton's own `floating` variant.
  floating?: boolean;
};

// 2026-08-04: the OTP-era "before contacting" ContactModal popup is gone from this
// button — every click (first AND repeat) goes through contactViaCompany(), which
// ALWAYS opens our own company WhatsApp with a tracked /tel/<code> link, never the
// pro directly. See lib/directContact.ts's top comment for the full incident —
// an earlier same-day version of this file called openCall(proPhone) directly (first
// click via a device check, repeat click unconditionally), which the user caught live
// in production as a serious bug: visitors must NEVER be routed straight to a
// business owner, only ever through our own WhatsApp number. ContactModal.tsx itself
// implements this same pattern with a popup step, for any future surface that wants it.
// 2026-08-23 (user request): the button never swaps to a "נשלח" label after being
// clicked — same behavior for a repeat call as the first one. If the visitor wants to
// call again, they just press the same button again.
export default function ContactButton({ proName, proFirstName, proPhone, proId, proGender, categorySlug, categoryName, className, label, square, floating, skin }: BtnProps) {
  const [loading, setLoading] = useState(false);
  const liveCtx = useSmartIntakeCtx();
  const displayName = proFirstName || proName.split(/[\s\-,]+/)[0];
  const text = label ?? (callLabel(displayName) as string); // the ONE builder (lib/heMsg.mjs)

  // text-[15px] (2026-08-22 user report): matches WaSmartButton's active full-mode
  // button size exactly (components/WaSmartButton.tsx's `!WA_TYPING_PREVIEW_ENABLED`
  // branch) — this default fallback (used on the pro's own page, no className passed)
  // previously had no explicit size at all, so it fell back to the ambient/inherited
  // size instead of matching the WhatsApp button right above it.
  const cls = className ?? 'flex items-center gap-2 justify-center w-full py-2.5 px-4 rounded-xl font-bold text-white text-[15px]';

  async function handleClick() {
    if (!proId || loading) return;
    setLoading(true);
    fireContactIntent(proId, proName, categorySlug, 'call');
    // ROOT FIX 02/10/26 (13723/13724): the call door carried NO city and NO need → the pro got "פרטי הפנייה: עבודה".
    // Same source as WaSmartButton: THE page city (resolvePageCity) + the questionnaire's cleanNeed when it is this category.
    const city = resolvePageCity(liveCtx) || liveCtx?.city || null;
    const need = liveCtx && ctxIsSameCategory(liveCtx as any, categoryName) ? ((liveCtx as any).cleanNeed || null) : null;
    await contactViaCompany({ proId, proGender, displayName, categorySlug, categoryName, intent: 'call', city, need });
    setContacted(proId, proPhone, 'call');
    setLoading(false);
  }

  if (skin) {
    return <button type="button" onClick={handleClick} disabled={loading} aria-label={skin.label} title={skin.label} className={skin.className} style={skin.style}>{skin.children}</button>;
  }

  if (floating) {
    return (
      <button onClick={handleClick} disabled={loading} title="התקשר" aria-label="התקשר"
        className="flex items-center justify-center rounded-full flex-shrink-0"
        style={{ background: '#2563eb', border: 'none', cursor: 'pointer', width: 52, height: 52, boxShadow: '0 4px 16px rgba(0,0,0,.25)' }}>
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      </button>
    );
  }

  if (square) {
    return (
      <button onClick={handleClick} disabled={loading}
        className="flex flex-col items-center justify-center gap-0.5 rounded-xl flex-shrink-0"
        style={{ background: '#2563eb', border: 'none', cursor: 'pointer', width: 50, height: 50 }}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
        <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', lineHeight: 1 }}>התקשר</span>
      </button>
    );
  }

  return (
    <button onClick={handleClick} className={cls} style={{ background: '#2563eb' }} disabled={loading}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
      {loading ? '...' : text}
    </button>
  );
}
